(() => {
  'use strict';

  const loaderScript = document.currentScript;
  const sources = {
    clarity: loaderScript?.dataset.claritySrc,
    brandAgent: loaderScript?.dataset.clarityBrandAgentSrc,
    judgeMe: loaderScript?.dataset.judgemeSrc,
  };
  const judgeMeSelector = [
    '.jdgm-widget',
    '.judgeme-preview-badge',
    '#judgeme_product_reviews',
    '[class^="jdgm-"]',
    '[class*=" jdgm-"]',
  ].join(',');
  const judgeMeViewportSelector = [
    '.jdgm-review-widget',
    '.jdgm-all-reviews-page',
    '.jdgm-all-reviews-widget',
    '#judgeme_product_reviews',
    '[data-widget="review"]',
    '[data-widget="all-reviews-v2025"]',
  ].join(',');

  const states = { clarity: 'idle', judgeMe: 'idle' };
  const promises = {};

  const appendScript = (src, attributes = {}) =>
    new Promise((resolve, reject) => {
      if (!src) {
        reject(new Error('Deferred app source is missing'));
        return;
      }

      const existing = Array.from(document.scripts).find((candidate) => candidate.src === src);
      if (existing) {
        resolve(existing);
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      Object.assign(script, attributes);
      script.dataset.deferredApp = 'true';
      script.addEventListener(
        'load',
        () => {
          script.dataset.deferredLoaded = 'true';
          resolve(script);
        },
        { once: true },
      );
      script.addEventListener('error', () => reject(new Error(`Unable to load ${src}`)), { once: true });
      document.head.appendChild(script);
    });

  const setAppState = (app, state) => {
    states[app] = state;
    document.documentElement.dataset[app] = state;
  };

  const loadClarity = (reason = 'idle') => {
    if (promises.clarity) return promises.clarity;

    setAppState('clarity', 'loading');
    promises.clarity = Promise.all([
      appendScript(sources.clarity, { async: true }),
      appendScript(sources.brandAgent, { async: true }),
    ])
      .then(() => {
        setAppState('clarity', 'ready');
        document.dispatchEvent(new CustomEvent('clarity:deferred-ready', { detail: { reason } }));
      })
      .catch((error) => {
        setAppState('clarity', 'error');
        console.error('[Clarity] Deferred load failed:', error);
        throw error;
      });

    return promises.clarity;
  };

  const loadJudgeMe = (reason = 'idle') => {
    if (promises.judgeMe) return promises.judgeMe;

    setAppState('judgeMe', 'loading');
    promises.judgeMe = appendScript(sources.judgeMe)
      .then(() => {
        setAppState('judgeMe', 'ready');
        document.dispatchEvent(new CustomEvent('judgeme:deferred-ready', { detail: { reason } }));
      })
      .catch((error) => {
        setAppState('judgeMe', 'error');
        console.error('[Judge.me] Deferred load failed:', error);
        throw error;
      });

    return promises.judgeMe;
  };

  window.clarity = window.clarity || function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  const syncClarityConsent = (privacy) => {
    if (!privacy) return;
    window.clarity('consentv2', {
      ad_Storage: privacy.marketingAllowed() ? 'granted' : 'denied',
      analytics_Storage: privacy.analyticsProcessingAllowed() ? 'granted' : 'denied',
      source: 101,
    });
  };

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        console.error('[Clarity] Shopify consent API failed:', error);
        return;
      }
      syncClarityConsent(window.Shopify.customerPrivacy);
    });
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    window.clarity('consentv2', {
      ad_Storage: event.detail.marketingAllowed ? 'granted' : 'denied',
      analytics_Storage: event.detail.analyticsAllowed ? 'granted' : 'denied',
      source: 101,
    });
  });

  const closestJudgeMeTrigger = (target) =>
    target instanceof Element ? target.closest(judgeMeSelector) : null;

  const replayJudgeMeInteraction = (target) => {
    let attempts = 0;

    const replayWhenReady = () => {
      const widget = target.closest?.('.jdgm-widget, .judgeme-preview-badge');
      if (!target.isConnected) return;

      if (widget?.classList.contains('jdgm--done-setup') || attempts >= 60) {
        target.click?.();
        widget?.removeAttribute('aria-busy');
        return;
      }

      attempts += 1;
      window.setTimeout(replayWhenReady, 50);
    };

    requestAnimationFrame(replayWhenReady);
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (states.clarity === 'idle') loadClarity('pointerdown').catch(() => {});
      if (states.judgeMe === 'idle' && closestJudgeMeTrigger(event.target)) {
        loadJudgeMe('pointerdown').catch(() => {});
      }
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    'focusin',
    (event) => {
      if (states.clarity === 'idle') loadClarity('focus').catch(() => {});
      if (states.judgeMe === 'idle' && closestJudgeMeTrigger(event.target)) {
        loadJudgeMe('focus').catch(() => {});
      }
    },
    true,
  );

  document.addEventListener(
    'click',
    (event) => {
      if (states.judgeMe === 'ready' || states.judgeMe === 'error') return;

      const trigger = closestJudgeMeTrigger(event.target);
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      trigger.setAttribute('aria-busy', 'true');
      loadJudgeMe('interaction')
        .then(() => replayJudgeMeInteraction(event.target))
        .catch(() => trigger.removeAttribute('aria-busy'));
    },
    true,
  );

  const observeJudgeMeWidgets = () => {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        loadJudgeMe('viewport').catch(() => {});
      },
      { rootMargin: '400px 0px' },
    );

    document.querySelectorAll(judgeMeViewportSelector).forEach((widget) => observer.observe(widget));
  };

  const scheduleIdleLoad = () => {
    window.setTimeout(() => {
      const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1500));
      idle(() => loadClarity('idle').catch(() => {}), { timeout: 5000 });
      idle(() => loadJudgeMe('idle').catch(() => {}), { timeout: 7000 });
    }, 1500);
  };

  if (location.hash === '#judgeme' || /[?&]judgeme(?:_|=)/.test(location.search)) {
    loadJudgeMe('deep-link').catch(() => {});
  }

  observeJudgeMeWidgets();
  if (document.readyState === 'complete') scheduleIdleLoad();
  else window.addEventListener('load', scheduleIdleLoad, { once: true });

  window.DeferredApps = {
    loadClarity,
    loadJudgeMe,
    get states() {
      return { ...states };
    },
  };
})();
